import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { JobInfoContext } from "../../../job-info-context";
import { Results, Uniprot } from "../../../api/openapi-generator";
import { JobApi, ProteinApi } from "../../../api";
import { useFetchState } from "@loschmidt/commons/fetch-state";

type JobContextType = ReturnType<typeof useProviderValue>;

const JobContext = createContext<JobContextType | undefined>(undefined);

export const useJobContext = () => {
  const context = useContext(JobContext);
  if (context === undefined) {
    throw new Error("useJobContext must be used within a JobContextProvider");
  }
  return context;
};

export function JobContextProvider({children}: {
  children: ReactNode,
}) {
  const value = useProviderValue();
  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
}

function useProviderValue() {

  // Context:

  const ctx = useContext(JobInfoContext);

  const id = ctx.id;
  const predJobDone = ctx.jobInfo?.status === 5;
  const vinaJobDone = ctx.vinaJobInfo?.status === 5;
  const protein = ctx.jobInfo?.request.protein;
  const position = ctx.jobInfo?.request.position;

  // State:

  const [backendResults, setBackendResults] = useState<Results | null>(null);
  const [uniprot, setUniprot] = useState<Uniprot | null>(null);

  const {
    fetchState: beResultsFetchState,
    withFetchState: withBeResultsFetchState,
  } = useFetchState();
  const {
    fetchState: uniprotFetchState,
    withFetchState: withUniprotFetchState,
  } = useFetchState();

  // Effects:

  // Fetch results after the pred-job is done.
  // Re-fetch after the vina-job is done (vinaJobDone is in the dependency list).
  useEffect(() => {
    if (predJobDone) {
      withBeResultsFetchState(() => JobApi.results({ id: id }))
        .then(setBackendResults)
        .catch(err => console.error(err));
    }
  }, [id, predJobDone, vinaJobDone]);

  // Fetch uniprot data for a given protein and position.
  useEffect(() => {
    if (protein && position) {
      withUniprotFetchState(() => ProteinApi.getUniprot({ codeAscii: protein, position: position }))
        .then(uniprot => {
          // sort diseases so that annotations without disease heading are first
          if (uniprot.diseases) {
            // Boolean converts truthy/falsy value to bool
            // unary + converts bool to number
            uniprot.diseases.sort((a, b) => (+ Boolean(a.name || a.acronym)) - (+ Boolean(b.name || b.acronym)));
          }
          setUniprot(uniprot);
        })
        .catch(err => console.error(err));
    }
  }, [protein, position]);

  return {
    backendResultsNullable: backendResults,
    uniprotNullable: uniprot,
    backendResultsFetchState: beResultsFetchState,
    uniprotFetchState,
  };
}

